import type { FormEvent } from "react";
import { useEffect, useState } from "react";
import { Breadcrumb, Button, Container, Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";

import { AssignmentBox, Loading, Pagination, PracticeConfirmationModal } from "components";
import IconArrowBack from "components/Icons/IconArrowBack/IconArrowBack";
import IconBook from "components/Icons/IconBook/IconBook";
import PageHeading from "components/PageHeading/PageHeading";

import { useAppDispatch } from "store/hooks";
import { setAssignmentsListQueryParams } from "store/slices/queryParams";
import { removeResults } from "store/slices/topicPractice";

import type { UserSubjectFragment, UserTutoringMinimumFragment } from "api/generated";
import {
  useGetTutoringThemesMinimumQuery,
  useGetUserThemeByIdQuery,
  useLazyGetThemeAssignmentsQuery,
} from "api/generated";
import { getAdmissionTestLabel } from "utils/common/assignmentDetail";

import { useContentAccess } from "utils/hooks/useContentAccess";
import { useQuery } from "utils/hooks/useQuery";
import { useUserSubjects } from "utils/hooks/useUserSubjects";
import { processApiError } from "utils/processApiError";
import type { ISearchAssignment } from "utils/searchInAssignment";
import { searchInAssignment } from "utils/searchInAssignment";

import { usePracticeConfirmationModalLocalStorage } from "components/Modal/PracticeConfirmationModal/usePracticeConfirmationModalLocalStorage";
import { PAGE_QUERY_PARAM, SEARCH_QUERY_PARAM } from "const";

interface IAssignmentData extends ISearchAssignment {
  id: string;
}

const Themes = () => {
  const { t } = useTranslation(["common"]);
  const navigate = useNavigate();
  const { tutoringVisible } = useContentAccess();

  const { themeId } = useParams();

  if (themeId === undefined) {
    navigate("/");
    return null;
  }

  const dispatch = useAppDispatch();
  const { removeAllQueryParams, setEagerQueryParam, setSearchParam, searchParams } = useQuery();

  const {
    data: themeData,
    isLoading: isThemeLoading,
    error: apiThemeError,
  } = useGetUserThemeByIdQuery({ userThemeId: themeId });

  const [trigger, { data: assignmentsData, isLoading: isAssignmentsLoading, error: lazyAssignmentsError }] =
    useLazyGetThemeAssignmentsQuery();

  const { data: tutoringData, error: tutoringApiError } = useGetTutoringThemesMinimumQuery();

  const { userSubjects } = useUserSubjects();

  const [allAssignments, setAllAssignments] = useState<IAssignmentData[] | null>(null);
  const [foundAssignments, setFoundAssignments] = useState<IAssignmentData[] | null>(null);
  const [isModalVisible, setModalVisible] = useState(false);
  const { isConfirmationModalAllowed, setSeenModalToLocalStorage } = usePracticeConfirmationModalLocalStorage();
  const [subject, setSubject] = useState<UserSubjectFragment | null>(null);
  const [tutoring, setTutoring] = useState<UserTutoringMinimumFragment | null>(null);

  const search = searchParams.get(SEARCH_QUERY_PARAM);
  const pageString = searchParams.get(PAGE_QUERY_PARAM);
  const currentPage = typeof pageString === "string" ? Number(pageString) : 1;

  useEffect(() => {
    search === null ? setFoundAssignments(allAssignments) : searchAssignments(search);
  }, [allAssignments]);

  useEffect(() => {
    if (apiThemeError === undefined) return;

    processApiError(apiThemeError);
  }, [apiThemeError]);

  useEffect(() => {
    if (lazyAssignmentsError === undefined) return;

    processApiError(lazyAssignmentsError);
  }, [lazyAssignmentsError]);

  useEffect(() => {
    if (tutoringApiError === undefined) return;

    processApiError(tutoringApiError);
  }, [tutoringApiError]);

  useEffect(() => {
    const searchParam = searchParams.get(SEARCH_QUERY_PARAM);
    const pageParam = searchParams.get(PAGE_QUERY_PARAM);

    dispatch(
      setAssignmentsListQueryParams({
        topicId: themeId,
        params: {
          ...(typeof searchParam === "string" ? { [SEARCH_QUERY_PARAM]: searchParam } : null),
          ...(typeof pageParam === "string" ? { [PAGE_QUERY_PARAM]: pageParam } : null),
        },
      }),
    );
  }, [searchParams]);

  const searchAssignments = (searchedString: string) => {
    if (allAssignments === null) return;

    const found: IAssignmentData[] = [];

    for (const assignment of allAssignments) {
      if (searchInAssignment(assignment, searchedString)) {
        found.push(assignment);
      }
    }

    setFoundAssignments(found);
  };

  useEffect(() => {
    const themeId = themeData?.userTheme.theme?.id;
    if (!themeId) return;
    trigger({ themeId }).catch(() => {});
  }, [themeData]);

  useEffect(() => {
    if (assignmentsData === undefined) {
      setAllAssignments([]);
      return;
    }

    const assignmentsItems = assignmentsData.userAssignmentsCached.items.filter(assignment => assignment.envelope);
    const parsedAssignments: IAssignmentData[] = [];

    for (const assignment of assignmentsItems) {
      if (assignment.assignment?.isActive !== true) continue;

      parsedAssignments.push({
        id: assignment.id,
        text: assignment.assignment?.assignment?.assignmentText ?? "",
        questionText: assignment.assignment?.assignment?.questionsText ?? "",
        questionHeadings: assignment.assignment?.assignment?.questions.map(question => question.heading ?? "") ?? [],
        href: getTopicHref(assignment.id),
        testId: getAdmissionTestLabel(assignment.assignment?.general?.admissionTest) ?? null,
        testOrder: assignment.assignment?.general?.testOrder ?? null,
        envelopeType: assignment.envelope,
      });
    }

    setAllAssignments(parsedAssignments);
  }, [assignmentsData]);

  useEffect(() => {
    if (!userSubjects || !themeData?.userTheme?.theme?.subject) {
      return;
    }

    const subjectId = themeData.userTheme.theme.subject;
    const subjectOfTopic = userSubjects.find(item => item.subject?.id === subjectId)?.subject;

    setSubject(subjectOfTopic ?? null);
  }, [userSubjects, themeData]);

  useEffect(() => {
    if (!tutoringData?.userTutoringAll || !themeData?.userTheme?.theme?.id) {
      return;
    }

    const tutoringForTheme = tutoringData.userTutoringAll.find(
      tutoring => tutoring.theme === themeData.userTheme.theme?.id,
    );

    setTutoring(tutoringForTheme ?? null);
  }, [tutoringData, themeData]);

  useEffect(() => {
    if (allAssignments === null) return;

    if (search === null || search.length < 1) {
      if (searchParams.get(SEARCH_QUERY_PARAM) !== null) {
        removeAllQueryParams();
      }
      setFoundAssignments(allAssignments);

      return;
    }

    const timer = setTimeout(() => {
      searchAssignments(search);
    }, 250);

    return () => {
      clearTimeout(timer);
    };
  }, [search]);

  const getTopicHref = (assignmentId: string) => `/ulohy/${themeId}/${assignmentId}`;

  const handleSearch = (searched: string) => {
    setEagerQueryParam(SEARCH_QUERY_PARAM, searched);
  };

  const handlePageChange = (page: number) => {
    setSearchParam(PAGE_QUERY_PARAM, page.toString());
  };

  const handlePracticeStart = () => {
    dispatch(removeResults());
    navigate(`/procvicovani/tema/${themeId}`);
  };

  const handleSetModalVisible = () => {
    setSeenModalToLocalStorage();
    setModalVisible(true);
  };

  const submitForm = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
  };

  const pagesCount = foundAssignments === null ? 1 : Math.ceil(foundAssignments.length / 10);

  return (
    <>
      <div className={"page-header"}>
        <Container className={"container-mw-md"}>
          <Breadcrumb>
            <li className={"breadcrumb-item"}>
              <Link to="/ulohy">
                <IconArrowBack />
                {t("back", { ns: "common" })}
              </Link>
            </li>
          </Breadcrumb>

          <PageHeading customTitle={themeData?.userTheme?.theme?.appName ?? themeData?.userTheme?.theme?.name ?? ""} />

          <div className="page-header__inputs">
            <Button
              onClick={() => {
                isConfirmationModalAllowed ? handleSetModalVisible() : handlePracticeStart();
              }}
              disabled={
                assignmentsData?.userAssignmentsCached.items === undefined ||
                assignmentsData?.userAssignmentsCached.items.length === 0
              }
            >
              {t("trainThisTypeOfAssignments", { ns: "assignments" })}
            </Button>
            {/* AIDEV-NOTE: Maturant doučování nemá — bez `tutoringVisible` by odkaz vedl
                na routu, ze které ho guard z TutoringGuard.tsx hned vrátí zpátky. [TD-351] */}
            {subject && tutoring && tutoringVisible ? (
              <a href={`/doucovani/${subject.slug}/${tutoring.id}`} className="btn btn-azure d-flex align-items-center">
                <IconBook width="20" height="20" className={"me-2 icon"} />
                {t("title", { ns: "tutoring" })}
              </a>
            ) : null}
          </div>
        </Container>
      </div>

      <Container className={"container-mw-md page-container"} fluid>
        {isThemeLoading || isAssignmentsLoading ? (
          <Loading />
        ) : (
          <>
            <Form className="mb-3" onSubmit={submitForm}>
              <Form.Control
                className={"form-search"}
                placeholder={"Hledej v textu úlohy..."}
                value={search ?? ""}
                onChange={event => {
                  handleSearch(event.target.value);
                }}
              />
            </Form>

            {(foundAssignments ?? []).slice((currentPage - 1) * 10, currentPage * 10).map(assignment => (
              <AssignmentBox key={assignment.id} assignment={assignment} />
            ))}
            {pagesCount > 1 ? (
              <Pagination
                className={"mt-16px"}
                current={currentPage}
                total={pagesCount}
                onNavigation={handlePageChange}
              />
            ) : null}
          </>
        )}
      </Container>
      <PracticeConfirmationModal
        show={isModalVisible}
        onHide={() => {
          setModalVisible(false);
        }}
        onConfirm={handlePracticeStart}
      />
    </>
  );
};

export default Themes;
